/**
 * Memoized Message Item Component
 *
 * Individual message component that only re-renders when its own props change.
 * This significantly improves performance in long threads by preventing
 * unnecessary re-renders of all messages when typing.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import React, { memo } from "react";
import type { Message } from "@/types/chat";
import type { AIModel } from "@/lib/apiKeyStorage";
import type { FeatureId } from "@/types/features";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MessageActions from "./MessageActions";
import CodeBlock from "@/components/markdown/CodeBlock";
import { ToolCallDisplay } from "./ToolCallDisplay";
type MessageItemProps = {
  message: Message;
  index: number;
  totalMessages: number;
  currentModel?: AIModel;
  onRevertToMessage?: (messageId: string, switchToOriginalModel: boolean) => void;
  onRevertWithDraft?: (messageId: string, switchToOriginalModel: boolean) => void;
  onForkFromMessage?: (messageId: string) => void;
  messageActionsDisabled?: boolean;
  nextMessage?: Message;
  isFeatureEnabled?: (featureId: FeatureId) => boolean;
};
function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
}
const MessageItem: React.FC<MessageItemProps> = t0 => {
  const $ = _c(44);
  const {
    message: m,
    index,
    totalMessages,
    currentModel,
    onRevertToMessage,
    onRevertWithDraft,
    onForkFromMessage,
    messageActionsDisabled: t1,
    nextMessage,
    isFeatureEnabled
  } = t0;
  const messageActionsDisabled = t1 === undefined ? false : t1;
  const isUser = m.role === "user";
  const isSummary = m.content.startsWith("**Thread summary**");
  const isLastMessage = index === totalMessages - 1;
  const label = isUser ? "You" : isSummary ? "Summary" : m.role === "assistant" ? "Assistant" : "System";
  let t2;
  if ($[0] !== m.created_at) {
    t2 = formatTimestamp(m.created_at);
    $[0] = m.created_at;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const timestamp = t2;
  const hasAiResponseAfter = isUser && nextMessage?.role === "assistant";
  const t3 = `flex w-full ${isUser ? "justify-end" : "justify-start"} group`;
  let t4;
  if ($[2] !== currentModel || $[3] !== hasAiResponseAfter || $[4] !== index || $[5] !== isFeatureEnabled || $[6] !== isLastMessage || $[7] !== isUser || $[8] !== m.id || $[9] !== m.model || $[10] !== messageActionsDisabled || $[11] !== onForkFromMessage || $[12] !== onRevertToMessage || $[13] !== onRevertWithDraft || $[14] !== totalMessages) {
    t4 = onRevertToMessage && onForkFromMessage && onRevertWithDraft && currentModel && <div><MessageActions messageId={m.id} messageModel={m.model} isLastMessage={isLastMessage} isUserMessage={isUser} currentModel={currentModel} messagesAfterCount={totalMessages - index - 1} onRevert={onRevertToMessage} onRevertWithDraft={onRevertWithDraft} onForkFrom={onForkFromMessage} hasAiResponseAfter={hasAiResponseAfter} disabled={messageActionsDisabled} isFeatureEnabled={isFeatureEnabled} /></div>;
    $[2] = currentModel;
    $[3] = hasAiResponseAfter;
    $[4] = index;
    $[5] = isFeatureEnabled;
    $[6] = isLastMessage;
    $[7] = isUser;
    $[8] = m.id;
    $[9] = m.model;
    $[10] = messageActionsDisabled;
    $[11] = onForkFromMessage;
    $[12] = onRevertToMessage;
    $[13] = onRevertWithDraft;
    $[14] = totalMessages;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = `rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap shadow-sm ${isUser ? "bg-gray-100/80 text-foreground border border-gray-200/60 backdrop-blur-md" : isSummary ? "bg-secondary-mint/60 text-emerald-900 border border-secondary-mint backdrop-blur-md" : "bg-white/60 text-foreground border border-white/40 backdrop-blur-md"}`;
  let t6;
  if ($[16] !== label) {
    t6 = <span>{label}</span>;
    $[16] = label;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== timestamp) {
    t7 = timestamp && <span className="ml-2 normal-case text-[10px] opacity-75">{timestamp}</span>;
    $[18] = timestamp;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide opacity-70">{t6}{t7}</div>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t10;
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [remarkGfm];
    t10 = {
      code(t11) {
        const {
          node,
          className,
          children,
          ...props
        } = t11;
        const isInline = !className;
        return <CodeBlock inline={isInline} className={className} {...props}>{String(children).replace(/\n$/, "")}</CodeBlock>;
      },
      a(t12) {
        const {
          node: node_0,
          children: children_0,
          href,
          ...props_0
        } = t12;
        return <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline decoration-blue-400/50 hover:decoration-blue-300 transition-colors" {...props_0}>{children_0}</a>;
      }
    };
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  let t11;
  if ($[25] !== m.content) {
    t11 = <div className="prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-code:text-foreground"><ReactMarkdown remarkPlugins={t9} components={t10}>{m.content}</ReactMarkdown></div>;
    $[25] = m.content;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== m.tool_calls || $[28] !== m.tool_results) {
    t12 = m.tool_calls && m.tool_results && m.tool_calls.length > 0 && <ToolCallDisplay toolCalls={m.tool_calls} toolResults={m.tool_results} />;
    $[27] = m.tool_calls;
    $[28] = m.tool_results;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== m.citations) {
    t13 = m.citations && m.citations.length > 0 && <div className="mt-3 pt-3 border-t border-foreground-secondary/10"><div className="text-xs text-foreground-secondary/60 mb-2">Sources:</div><div className="flex flex-col gap-1">{m.citations.map(_temp)}</div></div>;
    $[30] = m.citations;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t5 || $[36] !== t8) {
    t14 = <div className={t5}>{t8}{t11}{t12}{t13}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t5;
    $[36] = t8;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t14 || $[39] !== t4) {
    t15 = <div className="flex flex-col gap-0 max-w-[70%]">{t4}{t14}</div>;
    $[38] = t14;
    $[39] = t4;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t15 || $[42] !== t3) {
    t16 = <div className={t3}>{t15}</div>;
    $[41] = t15;
    $[42] = t3;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
};

// Memoize the component to prevent unnecessary re-renders
// Only re-render when props actually change
export default memo(MessageItem, (prevProps, nextProps) => {
  // Custom comparison function for optimal performance
  // Note: isFeatureEnabled is compared by reference - when parent re-creates the function
  // (e.g., due to feature toggle changes), this will correctly trigger a re-render
  return prevProps.message.id === nextProps.message.id && prevProps.message.content === nextProps.message.content && prevProps.message.tool_calls === nextProps.message.tool_calls && prevProps.message.tool_results === nextProps.message.tool_results && prevProps.index === nextProps.index && prevProps.totalMessages === nextProps.totalMessages && prevProps.currentModel === nextProps.currentModel && prevProps.messageActionsDisabled === nextProps.messageActionsDisabled && prevProps.nextMessage?.id === nextProps.nextMessage?.id && prevProps.isFeatureEnabled === nextProps.isFeatureEnabled;
});
function _temp(citation, idx) {
  return <a key={idx} href={citation.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-500 underline decoration-blue-400/50 hover:decoration-blue-300 transition-colors flex items-start gap-1"><span className="opacity-60">[{idx + 1}]</span><span className="flex-1 break-all">{citation.title || citation.url}</span></a>;
}